import React from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { TableCustomFieldValue, createDefaultTableValue, parseTableValue } from '../utils/tableCustomField';

interface CustomTableFieldProps {
  value: unknown;
  onChange: (value: TableCustomFieldValue) => void;
  disabled?: boolean;
}

const CustomTableField: React.FC<CustomTableFieldProps> = ({ value, onChange, disabled = false }) => {
  const tableValue = parseTableValue(value);

  const updateCell = (rowIndex: number, colIndex: number, cellValue: string) => {
    const next = parseTableValue(tableValue);
    next.rows[rowIndex][colIndex] = cellValue;
    onChange(next);
  };

  const updateColumnName = (colIndex: number, columnName: string) => {
    const next = parseTableValue(tableValue);
    next.columns[colIndex] = columnName;
    onChange(next);
  };

  const addColumn = () => {
    const next = parseTableValue(tableValue);
    next.columns.push(`Coluna ${next.columns.length + 1}`);
    next.rows = next.rows.map((row) => [...row, '']);
    onChange(next);
  };

  const removeLastColumn = () => {
    const next = parseTableValue(tableValue);
    if (next.columns.length <= 1) return;
    next.columns = next.columns.slice(0, -1);
    next.rows = next.rows.map((row) => row.slice(0, -1));
    onChange(next);
  };

  const addRow = () => {
    const next = parseTableValue(tableValue);
    next.rows.push(next.columns.map(() => ''));
    onChange(next);
  };

  const removeLastRow = () => {
    const next = parseTableValue(tableValue);
    if (next.rows.length <= 1) return;
    next.rows = next.rows.slice(0, -1);
    onChange(next);
  };

  const resetTable = () => {
    onChange(createDefaultTableValue());
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <Button size="sm" variant="outline-primary" onClick={addColumn} disabled={disabled}>
          + Coluna
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={removeLastColumn}
          disabled={disabled || tableValue.columns.length <= 1}
        >
          - Coluna
        </Button>
        <Button size="sm" variant="outline-primary" onClick={addRow} disabled={disabled}>
          + Linha
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={removeLastRow}
          disabled={disabled || tableValue.rows.length <= 1}
        >
          - Linha
        </Button>
        <Button size="sm" variant="outline-danger" onClick={resetTable} disabled={disabled}>
          Limpar Tabela
        </Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <Table bordered size="sm" className="mb-0">
          <thead>
            <tr>
              {tableValue.columns.map((column, colIndex) => (
                <th key={`col-${colIndex}`} style={{ minWidth: 140 }}>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={column}
                    onChange={(e) => updateColumnName(colIndex, e.target.value)}
                    disabled={disabled}
                    placeholder={`Coluna ${colIndex + 1}`}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableValue.rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {tableValue.columns.map((_, colIndex) => (
                  <td key={`cell-${rowIndex}-${colIndex}`}>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={row[colIndex] ?? ''}
                      onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                      disabled={disabled}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default CustomTableField;
